import React, { ReactNode, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import i18n from 'src/config/i18n';

import { Button, Flex, Grid, Input, Skeleton, Text } from 'src/packages/core';
import { DarkTheme, LightTheme, ThemeType } from 'src/packages/theme';
import {
  ChevronRightIcon,
  ConvertIcon,
  DepositIcon,
  FileFilledIcon,
  SearchIcon,
  XCircleFilledIcon,
} from 'src/packages/icons';
import { config } from 'src/config';
import { goToUrl } from 'src/utils/go-to-url';
import { adjustKycAndFeaturesConfig } from 'src/utils/adjust-kyc-and-features-config';

import { KycAndFeaturesConfig, QuickSearchDataType } from '../Header.types';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';

interface SearchIconContentProps {
  theme: ThemeType;
  quickSearchData: QuickSearchDataType;
  kycAndFeaturesConfig?: KycAndFeaturesConfig;
}

interface CoinListItemProps {
  theme: ThemeType;
  baseName: string;
  quoteName: string;
  price: string;
  change: number;
  link: string;
  iconUrl?: string;
  clickable?: boolean;
}

interface FunctionListItemProps {
  theme: ThemeType;
  title: string;
  link: string;
  icon?: ReactNode;
  clickable?: boolean;
}

const Functions = [
  {
    label: i18n.t('header.menu.search_icon.section.function.function_deposit'),
    searchLabel: 'deposit',
    link: config.depositUrl,
    icon: DepositIcon,
  },
  {
    label: i18n.t('header.menu.search_icon.section.function.function_convert'),
    searchLabel: 'convert',
    link: config.convertUrl,
    icon: ConvertIcon,
  },
];

const ListItemContainer = styled((props) => <Flex {...props} />)<{ clickable?: boolean }>`
  ${({ theme, clickable = true }) => `
     &:hover {
       background-color: ${clickable ? theme.colors.background.secondary.BG4 : ''};
       cursor: ${clickable ? 'pointer' : 'auto'};
     }
  `}
`;

const SearchIconWrapper = styled((props) => <Flex {...props} />)`
  svg {
    :hover {
      cursor: auto;
      > path[fill] {
        fill: ${({ theme }) => theme.colors.text.tertiary} !important;
      }
    }
  }
`;

const CoinListItem = ({
  theme,
  baseName,
  quoteName,
  price,
  change,
  link,
  iconUrl,
  clickable,
}: CoinListItemProps) => {
  const currentTheme = theme === 'light' ? LightTheme : DarkTheme;
  return (
    <ListItemContainer
      width={'100%'}
      justifyContent={'space-between'}
      alignItems={'center'}
      padding={'14px 20px'}
      theme={currentTheme}
      clickable={clickable}
      onClick={() => clickable && goToUrl(link)}
    >
      <Flex alignItems={'center'} gap={'10px'}>
        {iconUrl ? (
          <img src={iconUrl} height={'24px'} width={'24px'} />
        ) : (
          <XCircleFilledIcon size={24} color={currentTheme.colors.text.tertiary} />
        )}
        <Flex>
          <Text text={baseName} color={currentTheme.colors.text.primary} variant='p2' />
          <Text text={quoteName} color={currentTheme.colors.text.tertiary} variant='p2' />
        </Flex>
      </Flex>
      <Flex flexDirection={'column'} alignItems={'flex-end'}>
        <Text text={price} color={currentTheme.colors.text.primary} variant='p2' />
        <Text
          text={`${change > 0 ? '+' : ''}${change.toFixed(2)}%`}
          color={change >= 0 ? currentTheme.colors.palette.green : currentTheme.colors.palette.red}
          variant='p3'
        />
      </Flex>
    </ListItemContainer>
  );
};

const RecommendedCoinListItem = ({
  theme,
  baseName,
  quoteName,
  price,
  change,
  link,
  iconUrl,
  clickable,
}: CoinListItemProps) => {
  const currentTheme = theme === 'light' ? LightTheme : DarkTheme;
  return (
    <ListItemContainer
      width={'100%'}
      padding={'16px 10px'}
      flexDirection={'column'}
      gap={'10px'}
      borderRadius={'8px'}
      background={currentTheme.colors.background.secondary.BG2}
      theme={currentTheme}
      clickable={clickable}
      onClick={() => clickable && goToUrl(link)}
    >
      <Flex alignItems={'center'} gap={'10px'}>
        {iconUrl ? (
          <img alt={'coinurl'} src={iconUrl} height={'24px'} width={'24px'} />
        ) : (
          <XCircleFilledIcon size={24} color={currentTheme.colors.text.tertiary} />
        )}
        <Flex>
          <Text text={baseName} color={currentTheme.colors.text.primary} variant='p2' />
          <Text text={quoteName} color={currentTheme.colors.text.tertiary} variant='p2' />
        </Flex>
      </Flex>
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Text text={price} color={currentTheme.colors.text.primary} variant='p2' />
        <Text
          text={`${change > 0 ? '+' : ''}${change.toFixed(2)}%`}
          color={change >= 0 ? currentTheme.colors.palette.green : currentTheme.colors.palette.red}
          variant='p3'
        />
      </Flex>
    </ListItemContainer>
  );
};

const FunctionListItem = ({
  theme,
  title,
  icon,
  link,
  clickable = true,
}: FunctionListItemProps) => {
  const currentTheme = theme === 'light' ? LightTheme : DarkTheme;
  return (
    <ListItemContainer
      width={'100%'}
      justifyContent={'space-between'}
      alignItems={'center'}
      padding={'14px 20px'}
      theme={currentTheme}
      clickable={clickable}
      onClick={() => clickable && goToUrl(link)}
    >
      <Flex alignItems={'center'} gap={'10px'}>
        {icon ? icon : <XCircleFilledIcon size={24} color={currentTheme.colors.text.tertiary} />}
        <Flex>
          <Text text={title} color={currentTheme.colors.text.primary} variant='p2' />
        </Flex>
      </Flex>
      <ChevronRightIcon size={12} color={currentTheme.colors.text.primary} />
    </ListItemContainer>
  );
};

export const SearchIconContent = ({
  theme,
  quickSearchData,
  kycAndFeaturesConfig,
}: SearchIconContentProps) => {
  const { t } = useTranslation();
  const { mediaQueries } = useTheme();
  const [searchKey, setSearchKey] = useState('');

  const currentTheme = theme === 'light' ? LightTheme : DarkTheme;
  const isMobile = useMediaQuery(mediaQueries.md);

  const {
    tradeSpotPlatformEnabled,
    tradeDerivativesPlatformEnabled,
    convertPlatformEnabled,
    enabledSymbols,
  } = adjustKycAndFeaturesConfig(kycAndFeaturesConfig);

  const recommendedSymbols = quickSearchData.spotSymbols.sort(
    (symbol1, symbol2) => symbol1.indexRecommendOrder - symbol2.indexRecommendOrder
  );

  const futuresSymbols = useMemo(() => {
    return quickSearchData.futureSymbols
      .filter(
        (currentSymbol) =>
          currentSymbol?.symbolName?.toLowerCase().includes(searchKey.toLowerCase()) ||
          currentSymbol?.tokenFullName?.toLowerCase().includes(searchKey.toLowerCase())
      )
      .sort((symbol1, symbol2) => symbol1.indexRecommendOrder - symbol2.indexRecommendOrder);
  }, [searchKey, quickSearchData.futureSymbols]);

  const spotSymbols = useMemo(() => {
    return quickSearchData.spotSymbols
      .filter(
        (currentSymbol) =>
          currentSymbol?.symbolName?.toLowerCase().includes(searchKey.toLowerCase()) ||
          currentSymbol?.tokenFullName?.toLowerCase().includes(searchKey.toLowerCase())
      )
      .sort((symbol1, symbol2) => symbol1.indexRecommendOrder - symbol2.indexRecommendOrder);
  }, [searchKey, quickSearchData.spotSymbols]);

  const filteredFunctions = useMemo(() => {
    return Functions.filter((fun) =>
      fun.searchLabel.toLowerCase().includes(searchKey.toLowerCase())
    );
  }, [searchKey]);

  return (
    <Flex
      width={isMobile ? '100%' : '361px'}
      gap={'24px'}
      flexDirection={'column'}
      backgroundColor={currentTheme.colors.background.secondary.BG3}
    >
      <Flex px={'20px'} pt={'24px'}>
        <Input
          theme={theme}
          placeholder={t('header.menu.search_icon.section.search.placeholder')}
          autoFocus
          width='100%'
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          leftElement={
            <SearchIconWrapper theme={currentTheme}>
              <SearchIcon color={currentTheme.colors.text.tertiary} />
            </SearchIconWrapper>
          }
          rightElement={
            searchKey && (
              <Flex onClick={() => setSearchKey('')}>
                <XCircleFilledIcon color={currentTheme.colors.text.tertiary} />
              </Flex>
            )
          }
        />
      </Flex>
      <Flex
        overflowY={'auto'}
        width={'100%'}
        flexDirection={'column'}
        maxHeight={isMobile ? 'max-content' : '66vh'}
        pb={'24px'}
      >
        {quickSearchData.loading ? (
          <ListItemContainer
            width={'100%'}
            flexDirection={'column'}
            gap={'10px'}
            alignItems={'center'}
            padding={'14px 20px'}
            clickable={false}
          >
            <Skeleton theme={theme} />
            <Skeleton theme={theme} />
            <Skeleton theme={theme} />
          </ListItemContainer>
        ) : searchKey ? (
          futuresSymbols.length > 0 || spotSymbols.length > 0 ? (
            <Flex flexDirection={'column'} gap={'24px'}>
              {spotSymbols.length > 0 && (
                <Flex gap={'8px'} flexDirection={'column'}>
                  <Text
                    text={t('header.menu.search_icon.section.spot.title')}
                    px={'20px'}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.primary
                        : DarkTheme.colors.text.primary
                    }
                    variant='h7'
                  />
                  <Flex flexDirection={'column'}>
                    {spotSymbols.map((symbol) => {
                      if (
                        !tradeSpotPlatformEnabled ||
                        (Array.isArray(enabledSymbols) &&
                          !enabledSymbols.includes(symbol.symbolName))
                      ) {
                        return null;
                      }

                      return (
                        <CoinListItem
                          baseName={symbol.baseTokenName}
                          quoteName={symbol.quoteTokenName}
                          price={symbol.price}
                          change={symbol.change}
                          theme={theme}
                          key={symbol.symbolId}
                          link={`${config.spotUrl}/${symbol.symbolName}`}
                          iconUrl={symbol.iconUrl}
                          clickable={symbol.canTrade}
                        />
                      );
                    })}
                  </Flex>
                </Flex>
              )}
              {futuresSymbols.length > 0 && (
                <Flex gap={'8px'} flexDirection={'column'}>
                  <Text
                    text={t('header.menu.search_icon.section.derivatives.title')}
                    px={'20px'}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.primary
                        : DarkTheme.colors.text.primary
                    }
                    variant='h7'
                  />
                  <Flex flexDirection={'column'}>
                    {futuresSymbols.map((symbol) => {
                      const quoteName = symbol.symbolName.replace(
                        symbol.secondLevelUnderlyingName,
                        ''
                      );

                      if (
                        !tradeDerivativesPlatformEnabled ||
                        (Array.isArray(enabledSymbols) &&
                          !enabledSymbols.includes(symbol.baseTokenName))
                      ) {
                        return null;
                      }

                      return (
                        <CoinListItem
                          baseName={symbol.secondLevelUnderlyingName}
                          quoteName={quoteName}
                          price={symbol.price}
                          change={symbol.change}
                          theme={theme}
                          key={symbol.symbolId}
                          link={`${config.derivativesUrl}/${symbol.baseTokenName}`}
                          iconUrl={symbol.iconUrl}
                          clickable={symbol.canTrade}
                        />
                      );
                    })}
                  </Flex>
                </Flex>
              )}
              <Flex px={'20px'}>
                <Button
                  theme={theme}
                  variant={'secondaryDefault'}
                  text={t('button.view_markets')}
                  width={'100%'}
                  textColor={currentTheme.colors.text.secondary}
                  onClick={() => {
                    goToUrl(`${config.coinflareUrl}/markets`);
                  }}
                />
              </Flex>
              {(futuresSymbols.length > 0 || spotSymbols.length > 0) && (
                <Flex gap={'8px'} flexDirection={'column'}>
                  <Text
                    text={t('header.menu.search_icon.section.function.title')}
                    px={'20px'}
                    color={currentTheme.colors.text.primary}
                    variant='h7'
                  />
                  <Flex flexDirection={'column'}>
                    {Functions.map((fun) => {
                      if (fun.searchLabel === 'convert' && !convertPlatformEnabled) {
                        return null;
                      }

                      return (
                        <FunctionListItem
                          key={fun.label}
                          title={fun.label}
                          link={fun.link}
                          icon={<fun.icon color={currentTheme.colors.text.primary} size={24} />}
                          theme={theme}
                          clickable={fun.searchLabel === 'convert' && convertPlatformEnabled}
                        />
                      );
                    })}
                  </Flex>
                </Flex>
              )}
            </Flex>
          ) : filteredFunctions.length > 0 &&
            'convert'.includes(searchKey.toLowerCase()) &&
            convertPlatformEnabled ? (
            <Flex gap={'8px'} flexDirection={'column'}>
              <Text
                text={t('header.menu.search_icon.section.function.title')}
                px={'20px'}
                color={currentTheme.colors.text.primary}
                variant='h7'
              />
              <Flex flexDirection={'column'}>
                {filteredFunctions.map((fun) => {
                  if (fun.searchLabel === 'convert' && !convertPlatformEnabled) {
                    return null;
                  }

                  return (
                    <FunctionListItem
                      key={fun.label}
                      title={fun.label}
                      link={fun.link}
                      icon={<fun.icon color={currentTheme.colors.text.primary} size={24} />}
                      theme={theme}
                    />
                  );
                })}
              </Flex>
            </Flex>
          ) : (
            <Flex flexDirection={'column'} gap={'24px'}>
              <Flex
                flexDirection={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                my={10}
                background={currentTheme.colors.background.secondary.BG1}
                borderRadius={'8px'}
              >
                <FileFilledIcon size={64} color={currentTheme.colors.border.lighter} />
                <Text
                  variant={'p2'}
                  text={t('header.menu.search_icon.no_data.text')}
                  color={currentTheme.colors.text.tertiary}
                  mt={2}
                />
              </Flex>
              {recommendedSymbols.length > 0 && (
                <Flex gap={'8px'} flexDirection={'column'}>
                  <Text
                    text={t('header.menu.search_icon.section.recommended_tokens.title')}
                    px={'20px'}
                    color={currentTheme.colors.text.primary}
                    variant='h7'
                  />
                  <Grid
                    gridTemplateColumns={'1fr 1fr'}
                    gridTemplateRows={'1fr 1fr'}
                    gap={'10px'}
                    paddingX={'20px'}
                  >
                    {recommendedSymbols.slice(0, 4).map((symbol) => {
                      if (
                        !tradeSpotPlatformEnabled ||
                        (Array.isArray(enabledSymbols) &&
                          !enabledSymbols.includes(symbol.symbolName))
                      ) {
                        return null;
                      }

                      return (
                        <RecommendedCoinListItem
                          baseName={symbol.baseTokenName}
                          quoteName={`/${symbol.quoteTokenName}`}
                          price={symbol.price}
                          change={symbol.change}
                          theme={theme}
                          key={symbol.symbolId}
                          link={`${config.spotUrl}/${symbol.symbolName}`}
                          iconUrl={symbol.iconUrl}
                          clickable={symbol.canTrade}
                        />
                      );
                    })}
                  </Grid>
                </Flex>
              )}
            </Flex>
          )
        ) : (
          <Flex gap={'24px'} flexDirection={'column'}>
            {tradeSpotPlatformEnabled && spotSymbols.length > 0 && (
              <Flex gap={'8px'} flexDirection={'column'}>
                <Text
                  text={t('header.menu.search_icon.section.popular_spot.title')}
                  px={'20px'}
                  color={currentTheme.colors.text.primary}
                  variant='h7'
                />
                <Flex flexDirection={'column'}>
                  {spotSymbols.slice(0, 3).map((symbol) => {
                    if (
                      !tradeSpotPlatformEnabled ||
                      (Array.isArray(enabledSymbols) && !enabledSymbols.includes(symbol.symbolName))
                    ) {
                      return null;
                    }

                    return (
                      <CoinListItem
                        baseName={symbol.baseTokenName}
                        quoteName={symbol.quoteTokenName}
                        price={symbol.price}
                        change={symbol.change}
                        theme={theme}
                        key={symbol.symbolId}
                        link={`${config.spotUrl}/${symbol.symbolName}`}
                        iconUrl={symbol.iconUrl}
                        clickable={symbol.canTrade}
                      />
                    );
                  })}
                </Flex>
              </Flex>
            )}
            {tradeDerivativesPlatformEnabled && futuresSymbols.length > 0 && (
              <Flex gap={'8px'} flexDirection={'column'}>
                <Text
                  text={t('header.menu.search_icon.section.popular_derivatives.title')}
                  px={'20px'}
                  color={currentTheme.colors.text.primary}
                  variant='h7'
                />
                <Flex flexDirection={'column'}>
                  {futuresSymbols.slice(0, 3).map((symbol) => {
                    const quoteName = symbol.symbolName.replace(
                      symbol.secondLevelUnderlyingName,
                      ''
                    );

                    if (
                      !tradeDerivativesPlatformEnabled ||
                      (Array.isArray(enabledSymbols) &&
                        !enabledSymbols.includes(symbol.baseTokenName))
                    ) {
                      return null;
                    }

                    return (
                      <CoinListItem
                        baseName={symbol.secondLevelUnderlyingName}
                        quoteName={quoteName}
                        price={symbol.price}
                        change={symbol.change}
                        theme={theme}
                        key={symbol.symbolId}
                        link={`${config.derivativesUrl}/${symbol.baseTokenName}`}
                        iconUrl={symbol.iconUrl}
                        clickable={symbol.canTrade}
                      />
                    );
                  })}
                </Flex>
              </Flex>
            )}
            <Flex px={'20px'}>
              <Button
                theme={theme}
                variant={'secondaryDefault'}
                text={t('button.show_more')}
                width={'100%'}
                textColor={currentTheme.colors.text.secondary}
                onClick={() => {
                  goToUrl(`${config.coinflareUrl}/markets`);
                }}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
